import { css } from '@emotion/react';
import {
  faCircleInfo, faScaleBalanced, faSync, faValueAbsolute,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type ForwardedRef, forwardRef,
  useMemo,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import {
  LottieAnimationComponent, LottieAnimationTypes,
} from '~/_shared/baseComponents/lottieAnimation';
import {
  ToggleComponent, ToggleSize,
} from '~/_shared/baseComponents/toggle';
import {
  TooltipBehavior, TooltipComponent,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { OverlayLottieAnimationComponent } from '~/_shared/components/overlay/overlayLottieAnimation.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import type { ChartItem } from './chartItem.types';
import { ChartItemVisualizer } from './chartItemVisualizer.component';

const containerStyle = (theme: Theme) => css({
  backgroundColor: theme.backgroundColors.secondary,
  border: `1px solid ${theme.borderColors.activeItem}`,
  borderRadius: 4,
  height: 300,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const headerStyle = (theme: Theme) => css({
  backgroundColor: theme.backgroundColors.highlight,
  color: theme.textColors.primary,
  height: 38,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 7px',
  gap: 20,
});

const titleStyle = css({
  fontSize: 20,
  fontWeight: 500,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
});

const bottomPartStyle = css({
  padding: '0 10px 10px 10px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  position: 'relative',
});

const statusBarStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  fontSize: 8,
  height: 13,
  alignItems: 'center',
  gap: 4,
  cursor: 'default',
});

const statusBarItemStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  lineHeight: '13px',
  height: 13,
  alignItems: 'center',
  gap: 2,
});

const righSideHeaderActionsContainerStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  lineHeight: '14px',
  height: 14,
  fontSize: 14,
  alignItems: 'center',
  flexWrap: 'nowrap',
  gap: 11,
});

const statusBarNoteItemStyle = (theme: Theme) => css({
  color: theme.textColors.info,
});

const statusBarDangerItemStyle = (theme: Theme) => css({
  color: theme.textColors.warning,
});

const actionIconStyle = ({ isDisabled }: { isDisabled?: boolean }) => (theme: Theme) => css({
  color: isDisabled ? theme.textColors.disabled : theme.textColors.primary,
  cursor: isDisabled ? 'not-allowed' : 'default',
  fontSize: 14,
  height: 14,
  width: 14,
  '&:hover': {
    color: isDisabled ? theme.textColors.disabled : theme.textColors.linkOnHover,
  },
});

export const ChartItemComponent = forwardRef((props: ChartItem, ref: ForwardedRef<HTMLDivElement>) => {
  const {
    title,
    isSyncing,
    isLoading,
    isOutdated,
    shouldNormalize,
    metrics,
    onNormalizeToggleClick,
    onRefreshClick,
  } = props;

  const [t] = useTranslation();
  const theme = useTheme();

  const syncingChartColors = useMemo(() => ({
    bar1: theme.textColors.info,
    bar2: theme.textColors.info,
    bar3: theme.textColors.info,
    bar4: theme.textColors.info,
  }), [theme]);

  const isForceAbsoluteValue = metrics.barValues.length === 1;

  return (
    <div ref={ref} css={containerStyle}>
      <div css={headerStyle}>
        <div css={titleStyle}>
          {title}
        </div>
        <div css={righSideHeaderActionsContainerStyle}>
          {onNormalizeToggleClick && !isForceAbsoluteValue && (
            <ToggleComponent
              offIcon={faValueAbsolute}
              offIconTopOffset={1}
              onIcon={faScaleBalanced}
              onIconTopOffset={1}
              size={ToggleSize.M}
              isOn={!!shouldNormalize}
              onChange={(newValue: boolean) => onNormalizeToggleClick?.(newValue)}
            />
          )}
          <TooltipComponent tooltipContent={onRefreshClick ? undefined : t('charts.refresh.upToDate')}>
            <FontAwesomeIcon
              icon={faSync}
              spin={isSyncing}
              css={actionIconStyle({ isDisabled: !onRefreshClick })}
              onClick={onRefreshClick}
              title={t('charts.refresh.upToDate')}
            />
          </TooltipComponent>
        </div>
      </div>
      <div css={bottomPartStyle}>
        {isLoading && (
          <OverlayLottieAnimationComponent
            type={LottieAnimationTypes.BarGraph}
            size={120}
            autoplay
            loop
          />
        )}
        <div css={statusBarStyle}>
          {isOutdated && (
            <TooltipComponent
              tooltipContent={t('charts.outdated.tooltip')}
              behavior={TooltipBehavior.ShowOnHover}
            >
              <span css={[statusBarItemStyle, statusBarDangerItemStyle]}>
                <FontAwesomeIcon icon={faCircleInfo} />
                <span>{t('charts.outdated.label')}</span>
              </span>
            </TooltipComponent>
          )}
          {isSyncing && (
            <span css={[statusBarItemStyle, statusBarNoteItemStyle]}>
              <span>{t('Syncing')}</span>
              <span>
                <LottieAnimationComponent
                  type={LottieAnimationTypes.BarGraph}
                  size={7}
                  colors={syncingChartColors}
                  autoplay
                  loop
                />
              </span>
            </span>
          )}
        </div>
        <div>
          <ChartItemVisualizer
            {...props}
            shouldNormalize={isForceAbsoluteValue ? false : shouldNormalize}
          />
        </div>
      </div>
    </div>
  );
});
